import React, { useEffect } from 'react';
import { Drawer, Form, Input, Button, Space, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Gateway } from '../../../../redux/reducers/clients/clients.types';
import { fetchGatewaysRequest, clearGatewayStatus, updateGatewayRequest } from '../../../../redux/reducers/clients/clientsSlice';
import { RootState } from '../../../../redux/store';
import { colors } from '../../../../utils/colors/colors';
import { useTranslation } from 'react-i18next';

interface UpdateGatewayDrawerProps {
    open: boolean;
    onClose: () => void;
    gatewayId?: string;
    clientId: string;
}

const UpdateGatewayDrawer: React.FC<UpdateGatewayDrawerProps> = ({ open, onClose, gatewayId, clientId }) => {
    const { t } = useTranslation(['clients', 'common']);
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const { data: gateways, loading, success, error } = useSelector((state: RootState) => state.clients.gateways);

    const selectedGateway = (gateways || []).find((g: Gateway) => g.id === gatewayId);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (gatewayId) {
                dispatch(updateGatewayRequest({ gatewayId, data: values, clientId }));
            }
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const handleClose = () => {
        form.resetFields();
        dispatch(clearGatewayStatus());
        onClose();
    };

    useEffect(() => {
        if (open && selectedGateway) {
            form.setFieldsValue(selectedGateway);
        }
        if (!open) {
            form.resetFields();
        }
    }, [open, selectedGateway, form]);

    useEffect(() => {
        if (success && open) {
            notification.success({ message: t('clients:gateway_update_success') });
            dispatch(fetchGatewaysRequest(clientId));
            handleClose();
        }
        if (error) {
            notification.error({ message: t(error) });
            dispatch(clearGatewayStatus());
        }
    }, [success, error, dispatch, clientId, open]);

    return (
        <Drawer
            title={<span style={{ color: colors.blueitNavyBlue, fontWeight: 'bold' }}>{t("clients:edit_gateway")}</span>}
            placement="right"
            width={400} // A good default width
            onClose={handleClose}
            open={open}
            destroyOnClose
            footer={
                <Space style={{ float: 'right' }}>
                    <Button onClick={handleClose}>{t("common:cancel")}</Button>
                    <Button type="primary" style={{ background: colors.blueitLightBlue }} onClick={handleSave} loading={loading}>
                        {t("common:save")}
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical" requiredMark={false}>
                <Form.Item name="gsm" label={t('clients:gsm_number')} rules={[{ required: true, message: t('clients:field_is_required') }]}>
                    <Input placeholder={t('clients:enter_gsm_number')} />
                </Form.Item>
                <Form.Item name="gateway" label={t('clients:gateway')} rules={[{ required: true, message: t('clients:field_is_required') }]}>
                    <Input placeholder={t('clients:enter_gateway')} />
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default UpdateGatewayDrawer;